import type { GridFilterItem, GridFilterModel } from '@mui/x-data-grid-pro';

const buildPairKey = (field: string, value: unknown): string =>
  `${field}::${typeof value}::${value === null || value === undefined ? '' : String(value)}`;

/**
 * Drops `(field, 'not', V)` predicates whenever the same items list also
 * contains a positive predicate that selects V on the same field — `is V`,
 * `equals V`, or `isAnyOf [..., V, ...]`.
 *
 * Why: saved reports migrated from the legacy CMS2 sometimes contain the
 * default `not Complete` / `not Cancelled` predicates alongside a positive
 * filter the user added later (e.g. `isAnyOf [Complete]`). The pair is a
 * literal contradiction, so the BFF returns zero rows — or 502s — even
 * when the rest of the filter is valid. Recreating the same report through
 * the new UI does not reproduce the conflict, so this only needs to run
 * when filters are loaded from persisted report data.
 *
 * Predicates of the shape `is X + not Y` (different values) are left alone
 * — they are redundant but not contradictory.
 */
export const sanitizeSavedReportFilterItems = (
  items: GridFilterItem[] | undefined
): GridFilterItem[] => {
  if (!items?.length) return items ?? [];

  const positivePairs = new Set<string>();
  for (const item of items) {
    if (item.value === null || item.value === undefined) continue;
    if (item.operator === 'is' || item.operator === 'equals') {
      positivePairs.add(buildPairKey(item.field, item.value));
    } else if (item.operator === 'isAnyOf' && Array.isArray(item.value)) {
      for (const v of item.value) {
        if (v !== null && v !== undefined) positivePairs.add(buildPairKey(item.field, v));
      }
    }
  }
  if (positivePairs.size === 0) return items;

  const willDrop = items.some(
    (item) => item.operator === 'not' && positivePairs.has(buildPairKey(item.field, item.value))
  );
  if (!willDrop) return items;

  return items.filter(
    (item) => !(item.operator === 'not' && positivePairs.has(buildPairKey(item.field, item.value)))
  );
};

export function sanitizeSavedReportFilterModel(filterModel: GridFilterModel): GridFilterModel;
export function sanitizeSavedReportFilterModel(
  filterModel: GridFilterModel | undefined
): GridFilterModel | undefined;
export function sanitizeSavedReportFilterModel(
  filterModel: GridFilterModel | undefined
): GridFilterModel | undefined {
  if (!filterModel) return filterModel;
  const items = sanitizeSavedReportFilterItems(filterModel.items);
  return items === filterModel.items ? filterModel : { ...filterModel, items };
}

/**
 * Rewrite the `equals` operator to `=` for filter fields that map to *numeric*
 * (integer) columns, before the predicate goes on the wire to a list BFF.
 *
 * Why: the list BFFs translate `equals` to a SQL `ILIKE` — either api-services'
 * raw `mapOperatorToRawSQL`, or lib-framework `getSequelizeOperator` →
 * `Op.iLike` (used by api-inventory-mgmt, api-logistics, api-structures,
 * api-user-mgmt, api-utilities). `integer ILIKE text` is invalid in PostgreSQL,
 * so an `equals` filter on a numeric ID / number column errors and returns
 * nothing. `=` (→ `Op.eq` / a plain numeric comparison) is the numeric-safe
 * operator. Each list page passes its own set of numeric field keys, mirroring
 * that domain's integer columns.
 *
 * `contains` (and the other text operators) are intentionally NOT rewritten —
 * they have no numeric-safe equivalent and need a backend text-cast, so they
 * pass through unchanged (and stay non-functional on numeric columns until that
 * backend change lands).
 */
export const mapNumericEqualsOperatorForWire = (
  field: string | undefined,
  operator: string | undefined,
  numericFields: ReadonlySet<string>
): string | undefined =>
  field !== undefined && operator === 'equals' && numericFields.has(field) ? '=' : operator;

/**
 * Apply {@link mapNumericEqualsOperatorForWire} across a filter model's items.
 * Used by the export path, which serialises the same items as the list query
 * and therefore needs the same numeric-column fix.
 */
export const mapNumericFilterItemsForWire = <T extends { field?: string; operator?: string }>(
  items: readonly T[] | undefined,
  numericFields: ReadonlySet<string>
): T[] =>
  (items ?? []).map((item) => ({
    ...item,
    operator:
      mapNumericEqualsOperatorForWire(item.field, item.operator, numericFields) ?? item.operator,
  }));

/**
 * How a list BFF expects "exclude all of these values" to arrive on the wire.
 * The two list-endpoint families parse the query string differently, so the
 * correct multi-value `Is not` form differs:
 *
 *  - `'repeatNot'` — **api-services** (Services list). Its filter loop reads
 *    repeated same-field params as an array and applies each with a distinct
 *    bind index, AND-ing them (`col != $c AND col != $c1 AND …`). It has no
 *    `isNotAnyOf` case, so that operator would emit invalid SQL. Hence: emit
 *    one `not` item per value.
 *  - `'isNotAnyOf'` — the **lib-framework** BFFs (Structures, Parts, Movements,
 *    Shipments). They collapse repeated params via `String(filter)` before
 *    splitting on `,`, so repeated `not` params would lose every value but the
 *    first. Their `getSequelizeOperator` does map `isNotAnyOf` to an AND of
 *    `notILike` over every value. Hence: emit a single `isNotAnyOf` item.
 */
export type ListExclusionDialect = 'repeatNot' | 'isNotAnyOf';

interface ListFilterInput {
  /** The wire field key (an `AdvancedFilter.dataKey`). */
  dataKey: string;
  operator: string;
  /** Comma-joined selection from the list picker, e.g. `"A,B,C"`. */
  value: string;
  type: string;
}

/**
 * Expand one non-range advanced filter into the wire-ready `GridFilterItem`(s),
 * fixing two list-operator bugs that stem from list picks being comma-joined
 * into a single `field=op,A,B,C` query param:
 *
 *  1. **`Is not` excluded only the first value.** Both BFF families read a
 *     single value for `not` (api-services binds `data[1]`; lib-framework
 *     returns `{ [Op.not]: data[1] }`), so `not,A,B,C` excluded only `A`.
 *     Multi-value exclusions are re-emitted in the {@link ListExclusionDialect}
 *     the target BFF understands.
 *  2. **`Is any of` with nothing ticked hid blank rows.** An empty selection
 *     serialised to `isAnyOf,` (empty value), which the BFFs turn into
 *     `iLike '%%'` / `~* ''` — predicates that NULL rows fail, so blanks
 *     vanished. An empty `isAnyOf`/`not` is now dropped (a no-op), matching
 *     the guards the Structures/Movements BFFs already apply.
 *
 * Only genuine list selections are touched: `isAnyOf` (list fields and the
 * text/number "bulk paste" box) and `not` on a `list` field. A `not` on a date
 * field (a single value, not a CSV) and every other operator pass through
 * unchanged.
 */
export const buildAdvancedFilterItems = (
  adv: ListFilterInput,
  dialect: ListExclusionDialect
): GridFilterItem[] => {
  const passthrough: GridFilterItem[] = [
    { field: adv.dataKey, operator: adv.operator, value: adv.value },
  ];

  const isListSelection =
    adv.operator === 'isAnyOf' || (adv.operator === 'not' && adv.type === 'list');
  if (!isListSelection) return passthrough;

  // Re-split the comma-joined picks; drop empties so a stray trailing comma or
  // an all-blank selection can't reach the wire.
  const values = (adv.value ?? '')
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);

  if (values.length === 0) return []; // bug 2: empty selection is a no-op.

  if (adv.operator === 'isAnyOf') {
    return [{ field: adv.dataKey, operator: 'isAnyOf', value: values.join(',') }];
  }

  // operator === 'not' on a list field.
  if (values.length === 1) {
    return [{ field: adv.dataKey, operator: 'not', value: values[0] }];
  }
  return dialect === 'isNotAnyOf'
    ? [{ field: adv.dataKey, operator: 'isNotAnyOf', value: values.join(',') }]
    : values.map((v) => ({ field: adv.dataKey, operator: 'not', value: v }));
};

/** A range chip: `between` carries its bounds in a second pair of fields, and
 *  dates use their own pair because the picker writes ISO strings there. */
interface RangeFilterInput extends ListFilterInput {
  value2?: string;
  dateFrom?: string;
  dateTo?: string;
}

/**
 * The full chip-list → wire-items expansion: splits `between` chips into their
 * two bound items and routes everything else through `buildAdvancedFilterItems`.
 *
 * Every list page used to inline this loop, which is how the `buildAdvancedFilterItems`
 * bypass reached four pages in the first place. One implementation means a page
 * cannot opt out of the list-operator fixes by accident.
 */
export const advancedFiltersToItems = (
  advancedFilters: readonly RangeFilterInput[],
  dialect: ListExclusionDialect
): GridFilterItem[] => {
  const items: GridFilterItem[] = [];

  for (const adv of advancedFilters) {
    if (adv.operator !== 'between') {
      items.push(...buildAdvancedFilterItems(adv, dialect));
      continue;
    }

    const from = adv.dateFrom ?? adv.value;
    const to = adv.dateTo ?? adv.value2 ?? '';
    const [lower, upper] = adv.type === 'date' ? ['onOrAfter', 'onOrBefore'] : ['>=', '<='];

    // An open-ended range is legitimate — emit only the bound the user gave.
    if (from) items.push({ field: adv.dataKey, operator: lower, value: from });
    if (to) items.push({ field: adv.dataKey, operator: upper, value: to });
  }

  return items;
};
